import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import {
  HapId,
  PrimaryFormDateOfBirth,
  StyledButton,
} from "react-common-ui-library";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  CONTAINED,
  LINK_APPLICATION_RESULTS_ROUTE,
  LINK_APPLICATION_ROUTE,
  MODE_ALL,
  SUBMIT,
} from "../../../Constants/CommonConstants";
import {
  APPLICATION_ALREADY_EXIST,
  APPLICATION_ALREADY_LINKED,
  APPLICATION_LINKED_SUCCESSFULLY,
  HAP_TOOLTIP_TEXT,
  LINK_APPLICATION_FORM_ID,
  LINK_APPLICATION_SEARCH_BUTTON,
  LINK_APPLICATION_SEARCH_BUTTON_ID,
  NAME_DOB,
  NAME_HAP_ID,
  NO_APPLICATION_FOUND,
} from "../../../Constants/LinkMyApplicationConstants";
import { FLOW_TEXT_APPLICATION } from "../../../Constants/NoMatchFoundModalConstants";
import {
  SLICE_LINK_APPLICATION,
  THUNK_POST_LINK_APPLICATION,
} from "../../../Constants/SliceAndThunkConstants";
import {
  clearLinkApplicationResponse,
  postLinkApplication,
  saveLinkApplication,
} from "../../../Features/LinkApplicationSlice";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import { manageLinkApplicationSelector } from "../../../Selectors/LinkApplicationSelector";
import {
  StyledLinkApplicationFieldWrapperBox,
  StyledLinkApplicationSubmitButtonBox,
} from "../../../Styles/LinkApplication/LinkMyApplication";
import NoMatchFoundModal from "../../Common/NoMatchFoundModal/NoMatchFoundModal";
import LinkMyApplicationAlert from "./LinkMyApplicationAlert";

/**
 * @description Link My Application Form screen allows the user to search their application with hapId and date of birth
 * @returns {ReactNode} Renders the Link My Application Form Screen which allows the user search the application with hapId and date of birth
 */
const LinkMyApplicationForm = () => {
  const intl = useIntl();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { hapIdDataDateOfBirth, hapIdDataHapId } = useSelector((state) =>
    manageLinkApplicationSelector(state)
  );

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_LINK_APPLICATION,
      THUNK_POST_LINK_APPLICATION
    )
  );

  const methods = useForm({
    mode: MODE_ALL,
    defaultValues: {
      [NAME_DOB]: hapIdDataDateOfBirth,
      [NAME_HAP_ID]: hapIdDataHapId,
    },
  });

  const { isSuccess, description } = apiResponses;

  const [openNoMatchFoundModal, setOpenNoMatchFoundModal] = useState(false);

  const onSubmit = (data) => {
    dispatch(saveLinkApplication(data));
    dispatch(postLinkApplication(data));
  };

  useEffect(() => {
    if (isSuccess) {
      if (description === APPLICATION_LINKED_SUCCESSFULLY) {
        navigate(
          `/${LINK_APPLICATION_ROUTE}/${LINK_APPLICATION_RESULTS_ROUTE}`
        );
      }
      if (
        description === NO_APPLICATION_FOUND ||
        description === APPLICATION_ALREADY_LINKED ||
        description === APPLICATION_ALREADY_EXIST
      ) {
        setOpenNoMatchFoundModal(true);
      }
      dispatch(clearLinkApplicationResponse(THUNK_POST_LINK_APPLICATION));
    }
  }, [description, isSuccess, navigate, dispatch]);

  return (
    <FormProvider {...methods}>
      <form
        id={LINK_APPLICATION_FORM_ID}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <Stack spacing={{ xs: 5, md: 7 }}>
          <StyledLinkApplicationFieldWrapperBox>
            <Stack spacing={4}>
              <HapId tooltipText={HAP_TOOLTIP_TEXT} />
              <PrimaryFormDateOfBirth />
            </Stack>
          </StyledLinkApplicationFieldWrapperBox>
          <Stack spacing={{ xs: 5, md: 10 }}>
            <LinkMyApplicationAlert />
            <StyledLinkApplicationSubmitButtonBox>
              <StyledButton
                id={LINK_APPLICATION_SEARCH_BUTTON_ID}
                type={SUBMIT}
                variant={CONTAINED}
              >
                {intl.formatMessage({ id: LINK_APPLICATION_SEARCH_BUTTON })}
              </StyledButton>
            </StyledLinkApplicationSubmitButtonBox>
          </Stack>
        </Stack>
      </form>
      <NoMatchFoundModal
        flow={FLOW_TEXT_APPLICATION}
        open={openNoMatchFoundModal}
        setOpen={setOpenNoMatchFoundModal}
      />
    </FormProvider>
  );
};

export default LinkMyApplicationForm;
